import React, { useState } from 'react';
import {
  ConditionGroup,
  AnswerOptionCondition,
  AnswerOptionConditionType,
  isConditionGroup,
} from 'shared/types/due-diligence';
import { ConditionGroupRenderer } from './ConditionGroupRenderer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CaretDown, CaretRight, Plus, TreeStructure } from '@phosphor-icons/react';
import { createNewGroup, createNewCondition, countConditions, CONDITION_TYPES } from './condition-utils';

interface ConditionBuilderV2Props {
  conditionRoot: ConditionGroup | null;
  onChange: (root: ConditionGroup | null) => void;
  linkedLabels?: Array<{ id: number; name: string }>;
  extractionQuestions?: Array<{
    fieldName: string;
    displayName: string;
    extractionLabel?: string | null;
    extractionValueType?: 'number' | 'string' | 'date' | 'boolean' | null;
  }>;
  availableQuestions?: Array<{
    id: number;
    displayName: string;
    fieldName: string;
    answerOptions?: Array<{
      id?: number;
      label: string;
      value: string;
      statusColor: string;
      active: boolean;
    }>;
  }>;
  currentQuestionId?: number;
}

export const ConditionBuilderV2: React.FC<ConditionBuilderV2Props> = ({
  conditionRoot,
  onChange,
  linkedLabels = [],
  extractionQuestions = [],
  availableQuestions = [],
  currentQuestionId,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const hasMultipleLabels = linkedLabels.length > 1;

  // Filter available condition types based on context
  const availableConditionTypes = CONDITION_TYPES.filter((conditionType) => {
    if (conditionType.type === AnswerOptionConditionType.DATABASE_COMPARISON && extractionQuestions.length === 0) {
      return false;
    }
    if (conditionType.type === AnswerOptionConditionType.QUESTION_ANSWER && availableQuestions.length === 0) {
      return false;
    }
    return true;
  });

  // Recursive helper to find a group by ID
  const findGroup = (group: ConditionGroup, targetId: string): ConditionGroup | null => {
    if (group.id === targetId) return group;

    for (const child of group.children) {
      if (isConditionGroup(child)) {
        const found = findGroup(child, targetId);
        if (found) return found;
      }
    }

    return null;
  };

  // Recursive helper to find a node's parent group
  const findParentGroup = (group: ConditionGroup, targetId: string): ConditionGroup | null => {
    for (const child of group.children) {
      if (child.id === targetId) return group;

      if (isConditionGroup(child)) {
        const found = findParentGroup(child, targetId);
        if (found) return found;
      }
    }

    return null;
  };

  // Recursive helper to find a condition by ID
  const findCondition = (group: ConditionGroup, conditionId: string): AnswerOptionCondition | null => {
    for (const child of group.children) {
      if (child.id === conditionId && !isConditionGroup(child)) {
        return child as AnswerOptionCondition;
      }

      if (isConditionGroup(child)) {
        const found = findCondition(child, conditionId);
        if (found) return found;
      }
    }

    return null;
  };

  const handleAddCondition = (groupId: string, type: AnswerOptionConditionType) => {
    if (!conditionRoot) {
      // Create new root with first condition
      const newRoot = createNewGroup();
      const newCondition = createNewCondition(type);
      if (hasMultipleLabels) newCondition.labelId = linkedLabels[0].id;
      newRoot.children.push(newCondition);
      onChange(newRoot);
      return;
    }

    const targetGroup = findGroup(conditionRoot, groupId);
    if (!targetGroup) return;

    const newCondition = createNewCondition(type);
    if (hasMultipleLabels) newCondition.labelId = linkedLabels[0].id;
    targetGroup.children.push(newCondition);
    onChange({ ...conditionRoot });
  };

  const handleAddGroup = (parentGroupId: string) => {
    if (!conditionRoot) return;

    const parentGroup = findGroup(conditionRoot, parentGroupId);
    if (!parentGroup) return;

    const newGroup = createNewGroup();
    parentGroup.children.push(newGroup);
    onChange({ ...conditionRoot });
  };

  const handleRemoveNode = (nodeId: string) => {
    if (!conditionRoot) return;

    const parentGroup = findParentGroup(conditionRoot, nodeId);
    if (!parentGroup) return;

    parentGroup.children = parentGroup.children.filter((child) => child.id !== nodeId);

    // If root group is now empty, set to null
    if (conditionRoot.children.length === 0) {
      onChange(null);
    } else {
      onChange({ ...conditionRoot });
    }
  };

  const handleUpdateCondition = (conditionId: string, updates: Partial<AnswerOptionCondition>) => {
    if (!conditionRoot) return;

    const condition = findCondition(conditionRoot, conditionId);
    if (!condition) return;

    Object.assign(condition, updates);
    onChange({ ...conditionRoot });
  };

  const handleChangeGroupLogic = (groupId: string) => {
    if (!conditionRoot) return;

    const group = findGroup(conditionRoot, groupId);
    if (!group) return;

    group.logic = group.logic === 'AND' ? 'OR' : 'AND';
    onChange({ ...conditionRoot });
  };

  const handleCreateInitialCondition = (type: AnswerOptionConditionType) => {
    const newRoot = createNewGroup();
    const newCondition = createNewCondition(type);
    if (hasMultipleLabels) newCondition.labelId = linkedLabels[0].id;
    newRoot.children.push(newCondition);
    onChange(newRoot);
  };

  const conditionCount = conditionRoot ? countConditions(conditionRoot) : 0;

  return (
    <div className="border border-gray-200 rounded-lg bg-white">
      {/* Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <CaretDown size={20} weight="bold" className="text-gray-500" />
          ) : (
            <CaretRight size={20} weight="bold" className="text-gray-500" />
          )}
          <TreeStructure size={20} weight="regular" className="text-gray-600" />
          <span className="font-medium text-gray-900">Voorwaarden</span>
          {conditionCount > 0 && (
            <Badge variant="secondary" className="ml-2">
              {conditionCount}
            </Badge>
          )}
        </div>
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="p-4 border-t border-gray-200">
          {conditionRoot ? (
            <ConditionGroupRenderer
              group={conditionRoot}
              depth={0}
              linkedLabels={linkedLabels}
              hasMultipleLabels={hasMultipleLabels}
              extractionQuestions={extractionQuestions}
              availableQuestions={availableQuestions}
              currentQuestionId={currentQuestionId}
              availableConditionTypes={availableConditionTypes}
              onAddCondition={handleAddCondition}
              onAddGroup={handleAddGroup}
              onRemoveNode={handleRemoveNode}
              onUpdateCondition={handleUpdateCondition}
              onChangeGroupLogic={handleChangeGroupLogic}
            />
          ) : (
            <div className="text-center py-8">
              <p className="text-sm text-gray-500 mb-4">
                Geen voorwaarden geconfigureerd
              </p>

              <Popover>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" size="sm">
                    <Plus size={16} weight="bold" className="mr-1" />
                    Voorwaarde toevoegen
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-2" align="center">
                  <div className="space-y-1">
                    {availableConditionTypes.map((conditionType) => (
                      <button
                        key={conditionType.type}
                        type="button"
                        onClick={() => handleCreateInitialCondition(conditionType.type)}
                        className="w-full flex items-start gap-3 p-3 rounded-lg hover:bg-gray-100 transition-colors text-left"
                      >
                        <div className="flex-shrink-0 text-gray-500">
                          <div className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900">
                            {conditionType.label}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {conditionType.description}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
